import { useEffect, useState } from "react";
import { Control, Controller } from "react-hook-form";
import { Autocomplete, Checkbox, Chip, FormControlLabel, createFilterOptions } from "@mui/material";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";

import { procurementReportApi } from "@/main/api";
import { getTranslateByKey, isNullUndefined } from "@/main/services/utils";
import { KeyValue } from "@/main/models/common";
import { DynamicReportUserGroupResponse } from "@/main/models/response";
import KogInputText from "@/main/components/shared/inputText/inputText";
import { Spinner } from "@/main/components/shared/inputAutoComplete/styles";
import { DynamicReportPermissionForm } from "../../../../registerDynamicReport";

import * as S from "./styles";

interface IUserGroupAutoCompleteProps {
	control: Control<DynamicReportPermissionForm>;
	companyType: KeyValue | null;
	company: KeyValue | null;
	country: KeyValue | null;
	onChangeCustom: () => void;
}

const filterUserGroups = createFilterOptions<DynamicReportUserGroupResponse>();

function containsUserGroup(userGroups: DynamicReportUserGroupResponse[], guidGroup: string) {
	return userGroups.some((userGroup) => userGroup.guidGroup === guidGroup);
}

function UserGroupAutoComplete({
	control,
	companyType,
	company,
	country,
	onChangeCustom,
}: IUserGroupAutoCompleteProps) {
	const [userGroupOptions, setUserGroupOptions] = useState<DynamicReportUserGroupResponse[]>([]);
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const isCompanySelected = !isNullUndefined(companyType) && !isNullUndefined(company);
	const generalGroupsLabel = getTranslateByKey("information.generalGroups");

	useEffect(() => {
		setUserGroupOptions([]);

		if (isNullUndefined(companyType) || isNullUndefined(company) || isNullUndefined(country)) {
			setIsLoading(false);
			return;
		}

		let isCurrentRequest = true;
		setIsLoading(true);
		procurementReportApi
			.getDynamicReportUserGroups({
				idCompanyType: Number(companyType.key),
				guidCompany: String(company.key),
				guidCountry: String(country.key),
			})
			.then((response) => {
				if (isCurrentRequest) setUserGroupOptions(response.data.result);
			})
			.finally(() => {
				if (isCurrentRequest) setIsLoading(false);
			});

		return () => {
			isCurrentRequest = false;
		};
	}, [companyType?.key, company?.key, country?.key]);

	return (
		<Controller
			name="userGroups"
			control={control}
			render={({ field: { value, onChange } }) => {
				const unofferedUserGroups = value.filter(
					(userGroup) => !containsUserGroup(userGroupOptions, userGroup.guidGroup),
				);
				const isAllSelected =
					userGroupOptions.length > 0 && userGroupOptions.every((option) => containsUserGroup(value, option.guidGroup));
				const isDisabled = !isCompanySelected || isLoading || (userGroupOptions.length === 0 && value.length === 0);

				function changeUserGroups(userGroups: DynamicReportUserGroupResponse[]) {
					onChange(userGroups);
					onChangeCustom();
				}

				function toggleAllUserGroups(isChecked: boolean) {
					if (!isChecked) return changeUserGroups(unofferedUserGroups);

					const unselectedOptions = userGroupOptions.filter((option) => !containsUserGroup(value, option.guidGroup));
					changeUserGroups([...value, ...unselectedOptions]);
				}

				return (
					<S.Container>
						<Autocomplete
							multiple
							disableCloseOnSelect
							disableClearable
							id="id-newPermission-userGroups"
							size="small"
							disabled={isDisabled}
							options={[...userGroupOptions, ...unofferedUserGroups]}
							value={value}
							filterOptions={(options, state) =>
								filterUserGroups(
									options.filter((option) => containsUserGroup(userGroupOptions, option.guidGroup)),
									state,
								)
							}
							groupBy={(option) => (option.isGeneral ? generalGroupsLabel : (company?.value ?? ""))}
							getOptionLabel={(option) => option.name}
							isOptionEqualToValue={(option, selectedUserGroup) => option.guidGroup === selectedUserGroup.guidGroup}
							noOptionsText={getTranslateByKey("information.noRecordsFound")}
							popupIcon={isLoading ? <Spinner /> : <KeyboardArrowDown />}
							onChange={(_, userGroups) => changeUserGroups(userGroups)}
							renderOption={(props, option, { selected }) => {
								const { key, ...optionProps } = props;
								return (
									<li key={key} {...optionProps}>
										<Checkbox color="primary" checked={selected} />
										{option.name}
									</li>
								);
							}}
							renderTags={(userGroups, getTagProps) =>
								userGroups.map((userGroup, index) => {
									const { key, ...tagProps } = getTagProps({ index });
									return (
										<Chip
											key={key}
											size="small"
											label={
												<>
													{userGroup.displayName}
													{!userGroup.active && (
														<S.InactiveTag>{getTranslateByKey("information.inactive")}</S.InactiveTag>
													)}
												</>
											}
											{...tagProps}
										/>
									);
								})
							}
							renderInput={(params) => <KogInputText {...params} label={getTranslateByKey("information.userGroup")} />}
						/>
						<S.AreaCheckboxAllUserGroups>
							<FormControlLabel
								label={getTranslateByKey("information.all")}
								disabled={isDisabled || userGroupOptions.length === 0}
								control={
									<Checkbox
										color="primary"
										checked={isAllSelected}
										onChange={(e) => toggleAllUserGroups(e.target.checked)}
										id="id-newPermission-checkbox-allUserGroups"
									/>
								}
							/>
						</S.AreaCheckboxAllUserGroups>
					</S.Container>
				);
			}}
		/>
	);
}

export default UserGroupAutoComplete;
